import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import MainLayout from '../../components/layout/MainLayout';
import Pagination from '../../components/common/Pagination';
import roleApi, { Role } from '../../services/api/role.api';
import applicationApi from '../../services/api/application.api';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  status: 'active' | 'inactive' | 'suspended' | 'invited';
  is_active: boolean;
  created_at: string;
  last_login: string | null;
  tenant: {
    id: string;
    name: string;
    subdomain: string;
  } | null;
  roles: string[];
}

interface Application {
  id: string;
  name: string;
  environment: string;
}

export default function UsersPage() {
  const { t } = useTranslation(['settings', 'common']);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [roles, setRoles] = useState<Role[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteFirstName, setInviteFirstName] = useState('');
  const [inviteLastName, setInviteLastName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoleIds, setInviteRoleIds] = useState<string[]>([]);
  const [inviteApplicationIds, setInviteApplicationIds] = useState<string[]>([]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    status: 'active' as 'active' | 'inactive' | 'suspended' | 'invited',
    roleIds: [] as string[],
    applicationIds: [] as string[],
  });

  useEffect(() => {
    loadUsers();
    fetchRoles();
    fetchApplications();
  }, []);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/v1/admin/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setUsers(data.data.users || []);
      }
    } catch (error) {
      console.error('Failed to load users', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await roleApi.getAll();
      const rolesData = response.data || response;
      setRoles(Array.isArray(rolesData) ? rolesData : []);
    } catch (err: any) {
      console.error('Roles fetch error:', err);
      setRoles([]);
    }
  };

  const fetchApplications = async () => {
    try {
      const response = await applicationApi.getAll();
      setApplications(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      console.error('Applications fetch error:', err);
      setApplications([]);
    }
  };

  const handleInvite = async () => {
    if (!inviteFirstName || !inviteLastName || !inviteEmail) {
      toast.error(t('common:validation.required'));
      return;
    }

    const loadingToast = toast.loading(t('settings:users.sendingInvite') || 'Sending invitation...');

    try {
      const response = await fetch(`${API_BASE_URL}/v1/users/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: inviteFirstName,
          lastName: inviteLastName,
          email: inviteEmail,
          roleIds: inviteRoleIds,
        }),
      });

      const data = await response.json();
      if (data.success) {
        // Assign applications if any were selected
        if (inviteApplicationIds.length > 0 && data.data?.id) {
          await fetch(`${API_BASE_URL}/v1/users/${data.data.id}/applications`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ applicationIds: inviteApplicationIds }),
          });
        }

        toast.success(t('settings:users.inviteSuccess') || 'Invitation sent successfully!', { id: loadingToast });

        setShowInviteModal(false);
        setInviteFirstName('');
        setInviteLastName('');
        setInviteEmail('');
        setInviteRoleIds([]);
        setInviteApplicationIds([]);
        loadUsers();
      } else {
        toast.error(data.error || t('settings:users.inviteError') || 'Failed to invite user', { id: loadingToast });
      }
    } catch (err: any) {
      toast.error(err.message || t('settings:users.inviteError') || 'Failed to invite user', { id: loadingToast });
    }
  };

  const handleResendInvite = async (userId: string, userEmail: string) => {
    const loadingToast = toast.loading(t('settings:users.resendingInvite') || 'Resending invitation...');

    try {
      const response = await fetch(`${API_BASE_URL}/v1/users/${userId}/resend-invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      if (data.success) {
        toast.success(t('settings:users.resendSuccess') || `Invitation email resent to ${userEmail}`, { id: loadingToast });
      } else {
        toast.error(data.error || t('settings:users.resendError') || 'Failed to resend invitation', { id: loadingToast });
      }
    } catch (err: any) {
      toast.error(err.message || t('settings:users.resendError') || 'Failed to resend invitation', { id: loadingToast });
    }
  };

  const filteredUsers = users.filter((user) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.email.toLowerCase().includes(searchLower) ||
      user.first_name?.toLowerCase().includes(searchLower) ||
      user.last_name?.toLowerCase().includes(searchLower) ||
      user.tenant?.name.toLowerCase().includes(searchLower)
    );
  });

  // Paginate results
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page
  };

  const getStatusBadge = (status: string) => {
    const statusColors: { [key: string]: string } = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      inactive: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
      suspended: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      invited: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    };

    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColors[status] || statusColors.inactive}`}>
        {t(`settings:users.status.${status}`)}
      </span>
    );
  };

  const getRoleBadges = (roles: string[]) => {
    if (!roles || roles.length === 0) {
      return <span className="text-xs text-gray-400">No roles</span>;
    }

    const roleColors: { [key: string]: string } = {
      'Admin': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      'Editor': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      'Viewer': 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
    };

    return (
      <div className="flex flex-wrap gap-1">
        {roles.map((role) => (
          <span
            key={role}
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${roleColors[role] || 'bg-gray-100 text-gray-800'}`}
          >
            {t(`settings:users.roleLabels.${role}`)}
          </span>
        ))}
      </div>
    );
  };

  const stats = {
    total: users.length,
    active: users.filter(u => u.is_active).length,
    inactive: users.filter(u => !u.is_active).length,
    withTenant: users.filter(u => u.tenant !== null).length,
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">System Users</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              View and manage all users in the system
            </p>
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('settings:users.inviteUser')}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Users</div>
            <div className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{stats.total}</div>
          </div>
          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Active</div>
            <div className="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">{stats.active}</div>
          </div>
          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Inactive</div>
            <div className="mt-2 text-3xl font-bold text-red-600 dark:text-red-400">{stats.inactive}</div>
          </div>
          <div className="rounded-lg bg-white p-6 shadow dark:bg-gray-800">
            <div className="text-sm font-medium text-gray-500 dark:text-gray-400">With Tenant</div>
            <div className="mt-2 text-3xl font-bold text-indigo-600 dark:text-indigo-400">{stats.withTenant}</div>
          </div>
        </div>

        {/* Search */}
        <div className="flex gap-4">
          <input
            type="text"
            placeholder="Search by name, email, or tenant..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          />
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-gray-800">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Tenant
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Roles
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Created
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
                      </div>
                    </td>
                  </tr>
                ) : paginatedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      No users found
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map((user) => (
                    <tr
                      key={user.id}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex flex-col">
                          <div className="font-medium text-gray-900 dark:text-white">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{user.email}</div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        {user.tenant ? (
                          <div className="flex flex-col">
                            <div className="font-medium text-gray-900 dark:text-white">{user.tenant.name}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">{user.tenant.subdomain}</div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">No tenant</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        {getRoleBadges(user.roles)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        {getStatusBadge(user.status)}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {user.last_login ? new Date(user.last_login).toLocaleDateString() : t('settings:users.neverLoggedIn')}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-right">
                        <div className="flex justify-end space-x-2">
                          {user.status === 'invited' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResendInvite(user.id, user.email);
                              }}
                              className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400"
                            >
                              {t('settings:users.resendInvite')}
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedUser(user);
                            }}
                            className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400"
                          >
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {filteredUsers.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalItems={filteredUsers.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
        </div>

        {/* Invite Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="w-full max-w-md max-h-[90vh] rounded-xl border border-gray-200 bg-white shadow-lg dark:border-gray-800 dark:bg-gray-900 flex flex-col">
              <div className="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-800">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{t('settings:users.invite.title')}</h3>
              </div>
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('settings:users.edit.firstName')}
                  </label>
                  <input
                    type="text"
                    value={inviteFirstName}
                    onChange={(e) => setInviteFirstName(e.target.value)}
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    placeholder="John"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('settings:users.edit.lastName')}
                  </label>
                  <input
                    type="text"
                    value={inviteLastName}
                    onChange={(e) => setInviteLastName(e.target.value)}
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    placeholder="Doe"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('settings:users.invite.email')}
                  </label>
                  <input
                    type="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    placeholder="user@example.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('settings:users.invite.role')}
                  </label>
                  <div className="space-y-3 border border-gray-300 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50">
                    {roles.length === 0 && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                        {t('common:loading')}...
                      </p>
                    )}
                    {roles.map((role) => (
                      <label key={role.id} className="flex items-start space-x-3 cursor-pointer hover:bg-white dark:hover:bg-gray-800 p-2 rounded-lg transition-colors">
                        <input
                          type="checkbox"
                          checked={inviteRoleIds.includes(role.id)}
                          onChange={(e) => {
                            const newIds = e.target.checked
                              ? [...inviteRoleIds, role.id]
                              : inviteRoleIds.filter((id) => id !== role.id);
                            setInviteRoleIds(newIds);
                          }}
                          className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {t(`settings:users.roleLabels.${role.name}`)}
                          </div>
                          {role.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {t(`settings:users.roleDescriptions.${role.name}`)}
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                {/* Application Access - Only show if not Admin */}
                {(() => {
                  const hasAdminRole = roles.some(role => inviteRoleIds.includes(role.id) && role.name === 'Admin');
                  if (hasAdminRole) {
                    return (
                      <div className="rounded-lg border border-indigo-200 bg-indigo-50 dark:border-indigo-900 dark:bg-indigo-900/20 p-4">
                        <div className="flex items-start space-x-3">
                          <svg className="h-5 w-5 text-indigo-600 dark:text-indigo-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <div>
                            <p className="text-sm font-medium text-indigo-900 dark:text-indigo-100">
                              {t('settings:users.adminAccessNote')}
                            </p>
                            <p className="text-xs text-indigo-700 dark:text-indigo-300 mt-1">
                              {t('settings:users.adminAccessDescription')}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('settings:users.applicationAccess')}
                      </label>
                      <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800/50">
                        {applications.length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                            No applications available
                          </p>
                        ) : (
                          applications.map((app) => (
                            <label key={app.id} className="flex items-center space-x-2 cursor-pointer hover:bg-white dark:hover:bg-gray-800 p-2 rounded transition-colors">
                              <input
                                type="checkbox"
                                checked={inviteApplicationIds.includes(app.id)}
                                onChange={(e) => {
                                  const newIds = e.target.checked
                                    ? [...inviteApplicationIds, app.id]
                                    : inviteApplicationIds.filter(id => id !== app.id);
                                  setInviteApplicationIds(newIds);
                                }}
                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                {app.name} <span className="text-xs text-gray-500">({app.environment})</span>
                              </span>
                            </label>
                          ))
                        )}
                      </div>
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {t('settings:users.applicationAccessHint')}
                      </p>
                    </div>
                  );
                })()}
              </div>
              <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteFirstName('');
                    setInviteLastName('');
                    setInviteEmail('');
                    setInviteRoleIds([]);
                    setInviteApplicationIds([]);
                  }}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  {t('common:buttons.cancel')}
                </button>
                <button
                  onClick={handleInvite}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
                >
                  {t('settings:users.invite.send')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* User Detail Modal */}
        {selectedUser && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setSelectedUser(null)}>
            <div className="max-w-2xl max-h-[90vh] rounded-lg bg-white p-6 dark:bg-gray-800 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <h3 className="mb-4 text-xl font-bold text-gray-900 dark:text-white">
                User Details
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {selectedUser.first_name} {selectedUser.last_name}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedUser.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                    <p className="mt-1">{getStatusBadge(selectedUser.status)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Roles</label>
                    <p className="mt-1">{getRoleBadges(selectedUser.roles)}</p>
                  </div>
                  {selectedUser.tenant && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Tenant</label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedUser.tenant.name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Subdomain</label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">{selectedUser.tenant.subdomain}</p>
                      </div>
                    </>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Created At</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {new Date(selectedUser.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Last Login</label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {selectedUser.last_login ? new Date(selectedUser.last_login).toLocaleString() : t('settings:users.neverLoggedIn')}
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="rounded-md px-4 py-2 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
